import { useTranslation } from "react-i18next";
import { Box, makeStyles, Typography } from "@material-ui/core";
import Select from "react-select";
import Submit from "../../components/Submit";
import LoadingBackdrop from "../../components/LoadingBackdrop";
import { Formik } from "formik";
import * as Yup from "yup";

const useStyles = makeStyles(() => ({
  textLight: {
    fontFamily: "Montserrat, sans-serif",
    fontSize: "18px",
    fontWeight: 300,
    color: "#114668",
    alignSelf: "start",
    marginRight: "10px",
  },
  mainContainer: {
    display: "flex",
    flexDirection: "column",
    gap: "20px",
    padding: "10px",
    alignItems: "center",
  },
  horizontalBox: {
    width: "100%",
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
    gap: "15px",
  },
  verticalBox: {
    display: "flex",
    flexDirection: "column",
    alignItems: "end",
    gap: "8px",
  },
  errorBox: {
    width: "180px",
    color: "red",
  },
  divider: {
    height: "1px",
    backgroundColor: "#bcbcbc",
  },
  addEventButton: {
    width: "150px",
    height: "35px",
    border: "solid black 1px",
    color: "grey",
    backgroundColor: "white",
  },
  input: {
    width: "180px",
    padding: "8px",
    borderRadius: "4px",
    border: "1px solid #ccc",
  },
  select: {
    width: "180px",
    borderRadius: "4px",
    height: "auto",
  },
}));

const AddCalendarEventForm = ({
  onSubmit,
  initialValues,
  commandList,
  onSetEventActions,
  isLoading,
}) => {
  const { t } = useTranslation();
  const classes = useStyles();

  const validationSchema = Yup.object().shape({
    EVENT_NAME: Yup.string().required(
      t("create_calendar_event.required_field"),
    ),
    EVENT_DESCRIPTION: Yup.string().required(
      t("create_calendar_event.required_field"),
    ),
    EVENT_START_DATE: Yup.date().required(
      t("create_calendar_event.required_field"),
    ),
    EVENT_ACTION_LIST: Yup.array(Yup.number()).required(
      t("create_calendar_event.required_field"),
    ),
  });

  return (
    <>
      {isLoading && <LoadingBackdrop />}
      <Formik
        validationSchema={validationSchema}
        initialValues={initialValues}
        onSubmit={onSubmit}
      >
        {(props) => (
          <form onSubmit={props.handleSubmit} className={classes.mainContainer}>
            <Box className={classes.horizontalBox}>
              <Typography className={classes.textLight}>
                {t("create_calendar_event.name_label")}:
              </Typography>
              <Box className={classes.verticalBox}>
                <input
                  value={props.values.EVENT_NAME}
                  onChange={props.handleChange}
                  className={classes.input}
                  name="EVENT_NAME"
                />
                {props.errors.EVENT_NAME && props.touched.EVENT_NAME && (
                  <Box className={classes.errorBox}>
                    {props.errors.EVENT_NAME}
                  </Box>
                )}
              </Box>
            </Box>
            <Box className={classes.horizontalBox}>
              <Typography className={classes.textLight}>
                {t("create_calendar_event.description_label")}:
              </Typography>
              <Box className={classes.verticalBox}>
                <input
                  value={props.values.EVENT_DESCRIPTION}
                  onChange={props.handleChange}
                  className={classes.input}
                  name="EVENT_DESCRIPTION"
                />
                {props.errors.EVENT_DESCRIPTION &&
                  props.touched.EVENT_DESCRIPTION && (
                    <Box className={classes.errorBox}>
                      {props.errors.EVENT_DESCRIPTION}
                    </Box>
                  )}
              </Box>
            </Box>
            <Box className={classes.horizontalBox}>
              <Typography className={classes.textLight}>
                {t("create_calendar_event.date_and_time_label")}:
              </Typography>
              <Box className={classes.verticalBox}>
                <input
                  onChange={(e) =>
                    props.setFieldValue(
                      "EVENT_START_DATE",
                      new Date(e.target.value + ":00Z").getTime() / 1000,
                    )
                  }
                  className={classes.input}
                  type="datetime-local"
                  name="EVENT_START_DATE"
                />
                {props.errors.EVENT_START_DATE &&
                  props.touched.EVENT_START_DATE && (
                    <Box className={classes.errorBox}>
                      {props.errors.EVENT_START_DATE}
                    </Box>
                  )}
              </Box>
            </Box>
            <Box className={classes.horizontalBox}>
              <Typography className={classes.textLight}>
                {t("create_calendar_event.actions_label")}:
              </Typography>
              <Box className={classes.verticalBox}>
                <Select
                  closeMenuOnSelect={false}
                  isMulti={true}
                  isClearable={true}
                  isSearchable={false}
                  placeholder={t("create_calendar_event.select_actions")}
                  options={commandList}
                  onChange={(selectedOptions) => {
                    const selectedActions = onSetEventActions(selectedOptions);
                    props.setFieldValue("EVENT_ACTION_LIST", selectedActions);
                  }}
                  className={classes.select}
                  getOptionLabel={(option) => option.label}
                  getOptionValue={(option) => option.value}
                  name="EVENT_ACTION_LIST"
                />
                {props.errors.EVENT_ACTION_LIST &&
                  props.touched.EVENT_ACTION_LIST && (
                    <Box className={classes.errorBox}>
                      {props.errors.EVENT_ACTION_LIST}
                    </Box>
                  )}
              </Box>
            </Box>
            <div className={classes.divider} />
            <Submit
              {...props}
              className={classes.addEventButton}
              title={t("create_calendar_event.add_event")}
            />
          </form>
        )}
      </Formik>
    </>
  );
};

export default AddCalendarEventForm;
